import { useCallback, useEffect, useRef } from 'react';

import { type RovoMemoryEditsCountResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export interface UseMemoryEditsCountProps extends AssistanceServiceInjectedParams {
	/**
	 * Whether to enable fetching
	 */
	isEnabled: boolean;
}

export interface UseMemoryEditsCountReturn {
	/**
	 * The number of pending memory edits, or null if not yet loaded
	 */
	count: number | null;
	/**
	 * Whether the count is currently loading
	 */
	isLoading: boolean;
	/**
	 * Any error that occurred while fetching the count
	 */
	error: ApiError | null;
	/**
	 * Function to retry fetching the count
	 */
	onRetry: () => Promise<void>;
	refetch: () => Promise<RovoMemoryEditsCountResponse | null>;
}

/**
 * Hook for fetching the count of pending memory edits.
 */
export const useMemoryEditsCount: (
	props: Omit<UseMemoryEditsCountProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseMemoryEditsCountReturn = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		isEnabled,
	}: UseMemoryEditsCountProps): UseMemoryEditsCountReturn => {
		const { assistanceService } = assistanceServiceInjectedParams;

		const {
			data: countResponse,
			isFetching,
			refetch,
			error,
		} = useQuery<RovoMemoryEditsCountResponse>({
			queryKey: ['rovo-memory-edits-count'],
			enabled: isEnabled,
			queryFn: assistanceService.getRovoMemoryEditsCount,
		});

		// Refetch when the modal is reopened (isEnabled transitions false → true)
		const hasInitialized = useRef(false);
		useEffect(() => {
			if (!hasInitialized.current) {
				hasInitialized.current = true;
				return;
			}
			if (isEnabled) {
				refetch();
			}
		}, [isEnabled, refetch]);

		const handleRetry = useCallback(async () => {
			await refetch();
		}, [refetch]);

		return {
			count: countResponse?.count ?? null,
			isLoading: isFetching,
			error,
			onRetry: handleRetry,
			refetch,
		};
	},
);
